import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useDispatch, useSelector} from "react-redux"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {ColumnModel, SettingsList} from "../../../../components/Settings/SettingsList"
import {BridgeDeviceModel} from "../../../../types/bridge-device"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useLocalization} from "../../../../hooks/localization"
import {useParams} from "react-router-dom"
import {useQuestion} from "../../../../hooks/question"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {changeBusyAction} from "../../../../store/ui/actions"
import "./index.scss"


const keyExtractor = (item: BridgeDeviceModel) => item.id

export const BoosterDeviceListScreen = memo(function BoosterDeviceListScreen() {
    const {id} = useParams<{id: string}>()
    const {t} = useLocalization()
    const question = useQuestion()
    const {failure, success} = useMessage()
    const dispatch = useDispatch()
    const [rpc] = useRPC()
    const boostedList = useSelector(state => state.control.boosterList).filter(item => item?.booster_id === id)
    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceTypeList)
    const gatewaySet = useSetSelector(state => state.control.gatewayList)
    const deviceList = useSelector(state => state.control.bridgeDeviceList)
    const booster = deviceList.find(r => r.id === id)
    const [selectedDevice, setSelectedDevice] = useState<BridgeDeviceModel>(null)

    const filteredDeviceList = useMemo(() => {
        const list = []
        for (const newList of boostedList) {
            const filteredList = deviceList.filter(device => device?.id === newList?.boosted_device_id)
            list.push(...filteredList)
        }
        /*const newL = list.map(r => ({
            ...r,
            boosterId: boostedList.find(v => v.boosted_device_id === r.id).id
        }))*/

        return list
    }, [boostedList, deviceList])

    const displayNameExtractor = useCallback(
        (item: BridgeDeviceModel, column: ColumnModel) => {
            if (column.key === "type_id") {
                return bridgeDeviceSet[item?.type_id]?.name || "-"
            }
            if (column.key === "gateway_id") {
                return gatewaySet[item?.gateway_id]?.name || "-"
            }
            return item[column.key] || "-"
        },
        [bridgeDeviceSet, gatewaySet]
    )


    const onClickDelete = useCallback(
        (id: string) => {
            question(t("are_you_sure"), () => {
                dispatch(changeBusyAction(true))
                rpc.deleteDeviceFromBoostList(id)
                    .then(() => {
                        dispatch(changeBusyAction(false))
                        success(t("device_successfully_deleted"))
                    })
                    .catch(failure)
                    .finally(() => dispatch(changeBusyAction(false)))
            })
        },
        [question, dispatch, failure, success, t, rpc]
    )

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 3,
                name: t("device"),
                key: "name"
            },
            {
                length: 3,
                name: t("type"),
                key: "type_id"
            },
            {
                length: 2,
                name: t("rf_address"),
                key: "rf_address"
            },
            {
                length: 3,
                name: t("gateway"),
                key: "gateway_id"
            }
        ],
        [t]
    )

    const buttons = useMemo(
        () => [
            {
                text: t("add_device"),
                className: "green",
                url: `/settings/device/booster-list/${id}/add`
            },
            {
                text: t("delete"),
                className: "red",
                onClick: () => onClickDelete(selectedDevice?.id),
                hidden: !selectedDevice
            }
        ],
        [onClickDelete, t, id, selectedDevice]
    )
    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="device-list-page"
                backUrl={"/settings/device"}
                searchable={false}
            >
                <span className="booster-information">({booster?.name})</span>
                <SettingsList
                    columns={columns}
                    items={filteredDeviceList}
                    listType="boosterDeviceList"
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedDevice}
                    onItemSelected={setSelectedDevice}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
